import type { WorkspaceApp } from './app-shares';

/**
 * The pure id→app rule behind the `/app/<appId>` share link: the app to OPEN, or undefined when the
 * link lands on the Apps screen and stops there.
 *
 * <p>Mirrors iOS `AppRouter.resolvedAppToOpen` and Android `Navigator.resolvedAppToOpen` 1:1 — same
 * three nil cases, same precedence. The link is minted when an app is shared with somebody who may
 * not have a Symphony account yet, so it can name an app this reader owns, one they have only been
 * OFFERED, or one they have nothing to do with.
 *
 * <p>Undefined covers three different facts that all deserve the same behaviour — an unknown id, an id
 * that is only an unanswered OFFER, and a matched app with no openable url (the card lists it but its
 * body is not a control either). In all three the Apps screen already shows the reader whatever truth
 * there is, which is why the caller lands there FIRST and resolves after.
 *
 * <p><b>An offer wins over a match anywhere else.</b> An app still sitting in the offers list has not
 * been accepted, and opening it would skip the one decision the link exists to ask for — and would
 * skip the accept that credits the person who shared it.
 *
 * <p>Owned apps are matched before shared ones purely for determinism; an id in both is the same app.
 */
export function resolvedAppToOpen(
  appId: string | undefined,
  owned: readonly WorkspaceApp[],
  shared: readonly WorkspaceApp[],
  pending: readonly WorkspaceApp[],
): WorkspaceApp | undefined {
  if (!appId) return undefined;
  if (pending.some((app) => app.id === appId)) return undefined;
  const match = [...owned, ...shared].find((app) => app.id === appId);
  return match?.url ? match : undefined;
}

/**
 * The `?open=` value as a single id, or undefined.
 *
 * Next hands a repeated query key back as an array. Taking the first entry rather than refusing keeps
 * a hand-edited `?open=a&open=b` on the same screen it would otherwise land on anyway.
 */
export function openParam(value: string | string[] | undefined): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Whether every list the id must be resolved against has ANSWERED — a failed read included.
 *
 * <p>Its own function because the trap it guards is documented on the Apps screen and still caught
 * this code: `useAllWorkspaceApps([])` reports <b>ready-with-nothing</b> the instant it is handed an
 * empty id list, which is exactly what it gets while the Spaces are still in flight. So the owned-apps
 * state says "ready, no apps" about an account with eleven of them, and a resolver trusting it opens
 * nothing and silently strips the id — the link appears to do nothing at all, which is the failure it
 * was written to remove. The Spaces read has to be part of the answer.
 *
 * <p>"Answered" deliberately includes `error`: a failed read is a settled one, and waiting forever on
 * it would strand the reader on a screen that never acts on the link they followed.
 */
export function listsHaveAnswered(states: {
  workspaces: string;
  owned: string;
  shared: string;
  pending: string;
}): boolean {
  return Object.values(states).every((status) => status !== 'loading');
}

/** The in-app host page of a hosted app. A hosted app's MiniApp `url` points here too, for old binaries. */
export function hostedAppPath(appId: string): string {
  return `/apps/${encodeURIComponent(appId)}`;
}

/**
 * What tapping an app card does. A HOSTED app opens in Symphony's own host (same tab — it is part of the app, not
 * a third-party site); any other app keeps today's behaviour, its url in a new tab; an app with neither is not a
 * control at all.
 */
export function openTargetFor(
  app: Pick<WorkspaceApp, 'id' | 'url' | 'hosted'>,
): { kind: 'host'; path: string } | { kind: 'external'; url: string } | null {
  if (app.hosted) return { kind: 'host', path: hostedAppPath(app.id) };
  if (app.url) return { kind: 'external', url: app.url };
  return null;
}

/**
 * Where the `app/<id>` link (`/apps?open=<id>`) goes once the lists have answered.
 *
 * - A HOSTED app the reader has, or has only been OFFERED → its host page. The host shows the inline offer card
 *   ("<Owner> shared <App> with you") for an offer, so Accept still comes first; the offer is just answered where
 *   the app will open, which is the contract's rule on all three surfaces.
 * - A non-hosted app → exactly `resolvedAppToOpen` (an offer stays on the Apps screen, a match opens its url).
 */
export function appLinkTarget(
  appId: string | undefined,
  owned: readonly WorkspaceApp[],
  shared: readonly WorkspaceApp[],
  pending: readonly WorkspaceApp[],
): { kind: 'host'; path: string } | { kind: 'external'; app: WorkspaceApp } | undefined {
  if (!appId) return undefined;
  const hosted = [...pending, ...owned, ...shared].find((app) => app.id === appId && app.hosted);
  if (hosted) return { kind: 'host', path: hostedAppPath(appId) };
  const target = resolvedAppToOpen(appId, owned, shared, pending);
  return target ? { kind: 'external', app: target } : undefined;
}
